import type { WorkspaceToolParamView } from '../contracts/tool-view';

export type WorkspaceParamGroup = {
  kind: 'single' | 'switches' | 'paired-selects';
  fields: WorkspaceToolParamView[];
};

function isCompactSelect(field: WorkspaceToolParamView) {
  return field.type === 'select' && !field.controlVariant;
}

const RELATED_SELECT_PAIRS = [
  ['aspect_ratio', 'duration'],
  ['aspect_ratio', 'resolution'],
  ['width', 'height'],
] as const;

function areRelatedCompactSelects(
  first: WorkspaceToolParamView,
  second: WorkspaceToolParamView
) {
  return (
    isCompactSelect(first) &&
    isCompactSelect(second) &&
    RELATED_SELECT_PAIRS.some(
      ([left, right]) =>
        (first.id === left && second.id === right) ||
        (first.id === right && second.id === left)
    )
  );
}

function isFullWidthControl(field: WorkspaceToolParamView): boolean {
  return (
    field.type === 'segmented-slider' ||
    field.type === 'slider' ||
    field.type === 'multi-select' ||
    field.controlVariant === 'stepped-slider' ||
    field.controlVariant === 'tabs' ||
    field.controlVariant === 'upscale-size'
  );
}

/**
 * Presentation-only ordering: wide controls first, compact controls second.
 * Controllers that affect another visible field keep their earlier position so
 * users can choose a mode before adjusting its dependent parameters.
 */
export function orderWorkspaceParametersByFootprint(
  fields: WorkspaceToolParamView[]
): WorkspaceToolParamView[] {
  const controllerIds = new Set(
    fields.flatMap((field) =>
      field.activeWhen ? [field.activeWhen.fieldId] : []
    )
  );
  const wide = fields.filter(
    (field) => isFullWidthControl(field) || controllerIds.has(field.id)
  );
  const compact = fields.filter(
    (field) => !isFullWidthControl(field) && !controllerIds.has(field.id)
  );
  return [...wide, ...compact];
}

export function groupWorkspaceParameters(params: {
  fields: WorkspaceToolParamView[];
  pairAdjacentSelects: boolean;
}): WorkspaceParamGroup[] {
  const groups: WorkspaceParamGroup[] = [];
  const orderedFields = orderWorkspaceParametersByFootprint(params.fields);

  for (let index = 0; index < orderedFields.length; index += 1) {
    const current = orderedFields[index]!;
    const next = orderedFields[index + 1];

    if (
      params.pairAdjacentSelects &&
      next &&
      areRelatedCompactSelects(current, next)
    ) {
      groups.push({ kind: 'paired-selects', fields: [current, next] });
      index += 1;
      continue;
    }

    if (current.type === 'switch' && groups.at(-1)?.kind === 'switches') {
      groups.at(-1)!.fields.push(current);
      continue;
    }

    groups.push({
      kind: current.type === 'switch' ? 'switches' : 'single',
      fields: [current],
    });
  }

  return groups;
}
